import { Stepper } from '@workspace/ui/components/stepper'
import { toneText } from '@workspace/ui/lib/tone'
import { cn } from '@workspace/ui/lib/utils'
import type { HomeStateKey } from '../config/copy'
import { phaseIndex, RAIL_PHASES, RAIL_POSITION, toneForState } from '../config/rail'

/**
 * Where the founder is in the journey, plus the one-line substep beneath it.
 *
 * Read-only on purpose. The mockup's rail was clickable, but that was a
 * prototype control for flipping between states; shipping it would let someone
 * "navigate" to a phase they are not in, which has no defined meaning and
 * competes with the single CTA the whole page is built around.
 */
export function HomeProgressRail({
  stateKey,
  shareStepDone,
}: {
  stateKey: HomeStateKey
  /**
   * Whether the founder has ever had a qualifying organic view of their link
   * — true regardless of which state is current. Distinguishes a Share step
   * the founder actually completed from one an OUTREACH-intent founder simply
   * never reached: the former earns a checkmark, the latter renders as the
   * dashed/optional treatment instead of a false one.
   */
  shareStepDone?: boolean
}) {
  const current = phaseIndex(stateKey)
  const { detail } = RAIL_POSITION[stateKey]
  const tone = toneForState(stateKey)
  const currentPhase = RAIL_PHASES[current]

  const steps = RAIL_PHASES.map((phase, index) => ({
    id: phase.id,
    label: phase.label,
    done: index < current,
    optional: phase.id === 'share' && index < current && !shareStepDone,
  }))

  return (
    <div className="flex flex-col items-center gap-1.5">
      <Stepper
        compact
        steps={steps}
        currentId={currentPhase?.id ?? null}
        className="justify-center"
      />
      <p className="min-h-[18px] text-center text-[12px] text-muted-foreground">
        <span className={cn('font-semibold', toneText(tone))}>{currentPhase?.label}</span> ·{' '}
        {detail}
      </p>
    </div>
  )
}
